<script lang="ts">
  import {
    CarouselButtonGroup,
    CarouselProvider,
    CarouselStage,
    type CarouselStageProps,
  } from '@squareup/public-web-design-system';
  import type { PwNextHardwareCarouselProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import filterContent from '../PwNextPartials/helpers/filterContent';
  import { createLazyLoadGroup } from '$lib/actions/create-lazy-load-group';

  // prettier-ignore
  const {
    entryId,
    customCssClass,
    slides,
  }: PwNextHardwareCarouselProps = $props();

  const desktopSlideElements: HTMLElement[] = [];
  const mobileSlideElements: HTMLElement[] = [];
  let sidebarDesktopIndex: number = $state(0);

  const sharedCarouselConfig: CarouselStageProps = {
    options: {
      align: 'start',
      containScroll: false,
      loop: true,
    },
    slidesToShow: 1.8,
    slidesToShowOnMobile: 1,
  };

  function handleSlideChange({ currentIndex }: { currentIndex: number }) {
    sidebarDesktopIndex = currentIndex;

    const total = slides.length;

    for (let i = 0; i < total; i++) {
      const el = desktopSlideElements[i];

      if (i === currentIndex || i === (currentIndex + 1) % total) {
        el.style.opacity = '1';
      } else {
        el.style.opacity = '0';
      }
    }

    trackSlide(currentIndex);
  }

  function trackSlide(currentIndex: number) {
    // grab content from mobile since its easier to grab text
    const currentSlide = mobileSlideElements[currentIndex];
    const text = currentSlide.innerText;

    window.Sq.Tracking.track(
      {
        action: 'Slide View',
        category: 'Carousel',
        properties: {
          index: currentIndex,
          text,
        },
      },
      {
        destinations: ['googleAnalytics'],
      }
    );

    window.Sq.Tracking.track(
      {
        action: 'Advance Carousel',
        properties: {
          slideIndex: currentIndex,
          slideText: text,
        },
      },
      {
        destinations: ['cdp'],
      }
    );
  }
</script>

<section
  class="PwNextHardwareCarousel PwNextHardwareCarousel-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  use:createLazyLoadGroup
>
  {#if slides.length}
    <div class="PwNextHardwareCarousel__wrapper">
      <CarouselProvider>
        <div class="PwNextHardwareCarousel__sidebar-wrapper">
          {#each slides as slide, index}
            {@const elements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'LinkComponent']}
            <div class="PwNextHardwareCarousel__sidebar-content" class:active={index === sidebarDesktopIndex}>
              <DynamicChildrenComponent children={filterContent([slide], elements)} />
            </div>
          {/each}
          <CarouselButtonGroup />
        </div>
        <div class="PwNextHardwareCarousel__carousel-wrapper--desktop">
          <CarouselStage
            {...sharedCarouselConfig}
            slides={filterContent(slides, 'img')}
            onSlideChange={handleSlideChange}
            slideElements={desktopSlideElements}
          >
            {#snippet slide({ slide })}
              <DynamicChildrenComponent children={[slide]} />
            {/snippet}
          </CarouselStage>
        </div>
      </CarouselProvider>
      <CarouselProvider>
        <div class="PwNextHardwareCarousel__carousel-wrapper--mobile">
          <CarouselStage
            {...sharedCarouselConfig}
            {slides}
            onSlideChange={handleSlideChange}
            slideElements={mobileSlideElements}
          >
            {#snippet slide({ slide })}
              <DynamicChildrenComponent children={[slide]} />
            {/snippet}
          </CarouselStage>
          <CarouselButtonGroup />
        </div>
      </CarouselProvider>
    </div>
  {:else}
    <p>No slides found</p>
  {/if}
</section>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;

  @layer custom-components {
    .PwNextHardwareCarousel {
      --gutter-delta: 7.5px;
      display: flex;
      justify-content: center;
      position: relative;

      :global {
        .AproposImageComponent {
          margin-bottom: var(--space-20);

          @include mediaQueries.at('medium') {
            margin-bottom: 0;
          }
        }
      }
    }

    .PwNextHardwareCarousel__wrapper {
      display: flex;
      flex-direction: column-reverse;
      width: 100%;

      @include mediaQueries.at('medium') {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        max-width: 100%;
        align-items: center;
      }
    }

    .PwNextHardwareCarousel__sidebar-wrapper {
      background: var(--color-white);
      grid-column: 1 / span 4;
      position: relative;
      padding-left: max(
        calc(((100vw - var(--container-width-current)) / 2) - var(--gutter-delta)),
        0px
      ); // 92.5px gutter width
      padding-top: var(--space-20);
      display: none;

      @include mediaQueries.at('medium') {
        padding-top: 0;
        display: grid;
        height: fit-content;
        gap: var(--space-30);
      }
    }

    .PwNextHardwareCarousel__sidebar-content {
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
      transition: opacity var(--duration-quick) var(--easing-standard);
      grid-area: 1/1;
      grid-template-rows: min-content;
      height: fit-content;
      display: none;

      &.active {
        opacity: 1;
        pointer-events: all;
        visibility: visible;
      }

      @include mediaQueries.at('medium') {
        display: grid;
      }
    }

    .PwNextHardwareCarousel__carousel-wrapper--desktop {
      grid-column: 6 / span 8;
      height: 100%;
      max-height: 675px;
      display: none;

      @include mediaQueries.at('medium') {
        display: block;
      }
    }

    .PwNextHardwareCarousel__carousel-wrapper--mobile {
      display: block;

      @include mediaQueries.at('medium') {
        display: none;
      }

      :global {
        .CarouselButtonGroup {
          padding-left: max(calc(((100vw - var(--container-width-current)) / 2) - var(--gutter-delta)), 0px);
        }
      }
    }

    :global {
      .CarouselStage__slide {
        transition: opacity var(--duration-quick) var(--easing-standard);
      }

      .CarouselButtonGroup {
        align-items: flex-end;
        padding-top: var(--space-60);

        @include mediaQueries.at('medium') {
          padding-top: 0;
        }
      }
    }
  }
</style>
